import { Suspense, useMemo } from 'react';

import {
  Badge,
  Button,
  Icon,
  Stack,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  cn,
} from '@eluve/components';
import { LlmOutputTypeLabels } from '@eluve/llm-outputs';

import type { OutputTemplateOption } from '../PromptTemplateDefaultForm';
import type { PromptTemplateReference } from '../promptTemplateDefaults.operations';

import { EmptyState } from './EmptyState';
import { PanelHeader } from './PanelHeader';
import { UserPersonalSettingsCard } from './UserPersonalSettingsCard';
import type { PromptTemplateDefault } from './index';

interface DefaultsOverviewPanelProps {
  defaults: PromptTemplateDefault[];
  entityType: 'global' | 'org' | 'user';
  entityName?: string;
  onAddClick: () => void;
  selectedOutputType?: string | null;
  onSelectOutputType?: (outputType: string | null) => void;
  selectedUserId?: string;
  tenantId?: string;
  promptTemplates?: PromptTemplateReference[];
  outputTemplates?: OutputTemplateOption[];
}

interface OutputTypeGroup {
  outputType: string;
  label: string;
  count: number;
}

const ROW_BASE = 'cursor-pointer border-b border-border-tertiary px-3 py-2.5';

export const DefaultsOverviewPanel: React.FC<DefaultsOverviewPanelProps> = ({
  defaults,
  entityType,
  entityName,
  onAddClick,
  selectedOutputType,
  onSelectOutputType,
  selectedUserId,
  tenantId,
  promptTemplates,
  outputTemplates,
}) => {
  const isGlobal = entityType === 'global';
  const isOrg = entityType === 'org';
  const title = isGlobal ? 'Global Defaults' : isOrg ? 'Defaults' : 'Overrides';
  const tooltipText = isGlobal || isOrg ? 'Add Default' : 'Add Override';

  const isEmpty = defaults.length === 0;

  // Group defaults by output type and count
  const outputTypeGroups = useMemo(() => {
    const grouped: Record<string, number> = {};
    for (const def of defaults) {
      grouped[def.llmOutputType] = (grouped[def.llmOutputType] ?? 0) + 1;
    }

    const groups: OutputTypeGroup[] = Object.entries(grouped)
      .map(([outputType, count]) => ({
        outputType,
        label:
          LlmOutputTypeLabels[outputType as keyof typeof LlmOutputTypeLabels] ??
          outputType,
        count,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    return groups;
  }, [defaults]);

  return (
    <Stack align="stretch" fullHeight>
      <PanelHeader
        sticky
        cornerRadius="tl"
        title={
          <Stack gap={1} direction="horizontal">
            <Text inline weight="semibold">
              {title}
            </Text>
            <Tooltip>
              <TooltipTrigger asChild>
                <Icon
                  name="Info"
                  size="sm"
                  className="cursor-help text-text-tertiary"
                  aria-label="Defaults scope info"
                />
              </TooltipTrigger>
              <TooltipContent>
                {isGlobal
                  ? 'System-wide defaults apply unless overridden by org or user'
                  : isOrg
                    ? 'Defaults apply to all users unless overridden'
                    : `Overrides for ${entityName} take precedence`}
              </TooltipContent>
            </Tooltip>
          </Stack>
        }
        action={
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                onClick={onAddClick}
                aria-label={tooltipText}
              >
                <Icon name="Plus" size="auto" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{tooltipText}</TooltipContent>
          </Tooltip>
        }
      />

      {/* User Personal Settings Card */}
      {entityType === 'user' &&
        selectedUserId &&
        tenantId &&
        promptTemplates &&
        outputTemplates && (
          <Suspense
            fallback={
              <Stack className="px-3 py-3" gap={2}>
                <Text size="xs" color="tertiary">
                  Loading personal settings...
                </Text>
              </Stack>
            }
          >
            <div className="border-b border-border-tertiary">
              <UserPersonalSettingsCard
                tenantId={tenantId}
                userId={selectedUserId}
                userName={entityName ?? 'User'}
                promptTemplates={promptTemplates}
                outputTemplates={outputTemplates}
              />
            </div>
          </Suspense>
        )}

      {/* Content - Simple list like Apple Mail */}
      {isEmpty ? (
        <EmptyState
          message={`No ${isGlobal || isOrg ? 'defaults' : 'overrides'} configured`}
        />
      ) : (
        <Stack align="stretch">
          {outputTypeGroups.map((group) => {
            const isSelected = selectedOutputType === group.outputType;
            return (
              <Stack
                key={group.outputType}
                justify="between"
                align="start"
                onClick={() => onSelectOutputType?.(group.outputType)}
                className={cn(
                  ROW_BASE,
                  isSelected
                    ? 'bg-bg-accent-strong-primary text-white'
                    : 'hover:bg-bg-tertiary',
                )}
                gap={2}
                direction="horizontal"
              >
                <Stack align="start">
                  <Text
                    inline
                    size="xs"
                    className={isSelected ? 'text-white' : undefined}
                  >
                    {group.label}
                  </Text>
                  <code
                    className={cn(
                      'text-xs',
                      isSelected ? 'text-white/70' : 'text-text-tertiary',
                    )}
                  >
                    {group.outputType}
                  </code>
                </Stack>
                <Badge variant="secondary">{group.count}</Badge>
              </Stack>
            );
          })}
        </Stack>
      )}
    </Stack>
  );
};
